import { useState, useRef, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { AlertTriangle } from 'lucide-react';

export interface ThemeColors {
  primary: string;
  secondary: string;
  tertiary: string;
  onPrimary: string;
  surfaceAccent: string;
}

interface Props {
  value: ThemeColors;
  onChange: (next: ThemeColors) => void;
}

// WCAG relative luminance + contrast ratio. Returns ratio rounded to 1 decimal.
const relativeLuminance = (hex: string): number => {
  const h = hex.replace('#', '').trim();
  if (h.length !== 6) return 0;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 10) / 10;
};

const isValidHex = (v: string): boolean => /^#[0-9A-Fa-f]{6}$/.test(v);

interface PickerFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
}

// One labelled hex input with a click-to-open color picker popover.
const PickerField = ({ label, value, onChange }: PickerFieldProps) => {
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{ backgroundColor: isValidHex(value) ? value : '#FFFFFF' }}
          className="h-9 w-9 flex-shrink-0 rounded-md border border-gray-300 shadow-sm hover:ring-2 hover:ring-primary-300"
          aria-label={`Pick ${label}`}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const next = e.target.value.trim();
            if (/^#?[0-9A-Fa-f]{0,6}$/.test(next)) {
              onChange(next.startsWith('#') ? next : `#${next}`);
            }
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono uppercase shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          placeholder="#RRGGBB"
        />
      </div>
      {open && (
        <div ref={popRef} className="absolute left-0 z-20 mt-2 rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
          <HexColorPicker color={isValidHex(value) ? value : '#000000'} onChange={onChange} />
        </div>
      )}
    </div>
  );
};

export default function ProjectThemeEditor({ value, onChange }: Props) {
  const set = (k: keyof ThemeColors) => (next: string) => onChange({ ...value, [k]: next });

  const primaryOnPrimary = contrastRatio(value.primary, value.onPrimary);

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
      <h3 className="text-sm font-semibold text-gray-900">Theme Colors</h3>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PickerField label="Primary" value={value.primary} onChange={set('primary')} />
        <PickerField label="Secondary" value={value.secondary} onChange={set('secondary')} />
        <PickerField label="Tertiary" value={value.tertiary} onChange={set('tertiary')} />
        <PickerField label="On-Primary" value={value.onPrimary} onChange={set('onPrimary')} />
        <PickerField label="Surface Accent" value={value.surfaceAccent} onChange={set('surfaceAccent')} />
      </div>

      {primaryOnPrimary < 4.5 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            Text on Primary background fails WCAG AA ({primaryOnPrimary}:1, need ≥ 4.5:1).
            You can still save, but employees may struggle to read the header.
          </span>
        </div>
      )}
    </div>
  );
}
